/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import {
    CacheType,
    createObservableDataAction,
    getCatalogId,
    IAction,
    IActionContext,
    IActionInput,
    IAny,
    ICreateActionContext,
    IGeneric
} from '@msdyn365-commerce/core';
import { FeatureState } from '@msdyn365-commerce/retail-proxy';
import {
    getActivePricesAsync,
    getProductUnitsOfMeasureAsync
} from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import {
    AffiliationLoyaltyTier,
    PriceLookupContext,
    ProductPrice,
    ProductType,
    ProductUnitOfMeasure,
    ProjectionDomain,
    SimpleProduct,
    UnitOfMeasureSource
} from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import {
    ArrayExtensions,
    QueryResultSettingsProxy,
    getSelectedProductIdFromActionInput,
    getSelectedVariant,
    SelectedVariantInput
} from '@msdyn365-commerce-modules/retail-actions';
import { FeatureStateInput, getFeatureStateActionDataAction } from './get-feature-state';
import { getCartState } from '@msdyn365-commerce/global-state';

/**
 * Input class for the GetPriceForSelectedVariant Data Action.
 */
export class PriceForSelectedVariantUomsInput implements IActionInput {
    public productId: number;

    public channelId: number;

    public selectedProduct: SimpleProduct | undefined;

    public affiliationLoyaltyTiers: AffiliationLoyaltyTier[] | undefined;

    public customerId?: string;

    public constructor(
        productId: number,
        channelId: number,
        selectedProduct?: SimpleProduct,
        customerId?: string,
        affiliationLoyaltyTiers?: AffiliationLoyaltyTier[]
    ) {
        this.productId = productId;
        this.channelId = channelId;
        this.selectedProduct = selectedProduct;
        this.customerId = customerId ?? '';
        this.affiliationLoyaltyTiers = affiliationLoyaltyTiers;
    }

    public getCacheKey = (): string => 'PriceForSelectedVariantUomsInput';

    public getCacheObjectType = (): string => 'ProductUnitOfMeasure';

    public dataCacheType = (): CacheType => 'none';
}

/**
 * The createInput method for the GetPriceForSelectedVariantDataAction.
 * @param inputData - The input data for the createInput method.
 * @returns The PriceForSelectedVariantInput.
 */
export const createActivePriceForSelectedVariantUnitOfMeasuresInput = (
    inputData: ICreateActionContext<IGeneric<IAny>>
): PriceForSelectedVariantUomsInput => {
    const productId = getSelectedProductIdFromActionInput(inputData);

    if (productId) {
        return new PriceForSelectedVariantUomsInput(+productId, +inputData.requestContext.apiSettings.channelId, undefined);
    }
    throw new Error('Unable to create PriceForSelectedVariantInput, no productId found on module config or query');
};

/**
 * The Action Method for the GetPriceForSelectedVariant Data Action
 * Pulls the currently selected variant from the cache using the getSelectedVariant data action, and gets it's current contextual price
 * via the getActivePrice RetailServer API.
 * @param input - The input.
 * @param ctx - The ctx.
 * @returns The Promise<ProductPrice | null>.
 */
export async function getPricesForSelectedVariantUnitOfMeasures(
    input: PriceForSelectedVariantUomsInput,
    ctx: IActionContext
): Promise<ProductPrice[]> {
    return Promise.resolve()
        .then(() => {
            const activeProduct: SimpleProduct | undefined = input.selectedProduct;

            if (!activeProduct) {
                const selectedVariantInput = new SelectedVariantInput(
                    input.productId,
                    input.channelId,
                    undefined,
                    undefined,
                    ctx.requestContext
                );

                return getSelectedVariant(selectedVariantInput, ctx);
            }

            return activeProduct;
        })
        .then(async (productResult: SimpleProduct | null) => {
            const activeProduct: SimpleProduct | undefined = productResult as SimpleProduct | undefined;
            if (activeProduct) {
                const productId = activeProduct?.MasterProductId ? activeProduct?.MasterProductId : activeProduct?.RecordId;
                const unitsOfMeasure = await getProductUnitsOfMeasureAsync({ callerContext: ctx }, productId, {
                    IncludeQuantityLimitRules: false,
                    ExtensionProperties: []
                });
                const activeUoms =
                    unitsOfMeasure?.filter(
                        x =>
                            x.UnitOfMeasureSourceValue === UnitOfMeasureSource.Default ||
                            x.UnitOfMeasureSourceValue === UnitOfMeasureSource.IntraOrInterClassConversion
                    ) ?? [];
                return { productResult, uoms: activeUoms };
            }

            return { productResult, uoms: [] };
        })
        .then(async (value: { productResult: SimpleProduct | null; uoms: ProductUnitOfMeasure[] }) => {
            const catalogId = getCatalogId(ctx.requestContext);
            const projectDomain: ProjectionDomain = {
                ChannelId: +ctx.requestContext.apiSettings.channelId,
                CatalogId: catalogId
            };

            const activeProduct: SimpleProduct | undefined = value?.productResult as SimpleProduct | undefined;
            const uoms: ProductUnitOfMeasure[] = value?.uoms ?? [];
            if (activeProduct && uoms.length) {
                let productIds: number[] | undefined = [activeProduct.RecordId];
                let priceLookupContext: PriceLookupContext | undefined;
                const featureStates: FeatureState[] = await getFeatureStateActionDataAction(new FeatureStateInput(), ctx);

                let affiliationLoyaltyTiers: AffiliationLoyaltyTier[] | undefined;
                if (input?.affiliationLoyaltyTiers?.length) {
                    affiliationLoyaltyTiers = input?.affiliationLoyaltyTiers;
                }

                if (input.affiliationLoyaltyTiers === undefined) {
                    const cartState = await getCartState(ctx);
                    if (cartState?.cart?.AffiliationLines?.length) {
                        affiliationLoyaltyTiers = cartState.cart.AffiliationLines;
                    }
                }

                const pricesPromises = uoms.map(uom => {
                    if (uom) {
                        productIds = undefined;
                        const headerContext = affiliationLoyaltyTiers?.length
                            ? { AffiliationLoyaltyTierLines: affiliationLoyaltyTiers }
                            : undefined;
                        priceLookupContext = {
                            HeaderContext: headerContext,
                            LineContexts: [
                                {
                                    UnitOfMeasureSymbol: uom.Symbol,
                                    ProductRecordId: activeProduct.RecordId
                                }
                            ]
                        };
                    }
                    return getActivePricesAsync(
                        { callerContext: ctx, queryResultSettings: QueryResultSettingsProxy.getPagingFromInputDataOrDefaultValue(ctx) },
                        projectDomain,
                        productIds,
                        new Date(),
                        input.customerId,
                        undefined,
                        true,
                        featureStates.find(featureState => featureState.Name === 'Dynamics.AX.Application.RetailSearchPriceRangeFeature')
                            ?.IsEnabled &&
                            (activeProduct.ProductTypeValue === ProductType.Master ||
                                activeProduct.ProductTypeValue === ProductType.KitMaster),
                        featureStates.find(
                            featureState => featureState.Name === 'Dynamics.AX.Application.RetailSalesAgreementPriceSupportFeature'
                        )?.IsEnabled,
                        priceLookupContext
                    )
                        .then((productPrices: ProductPrice[]) => {
                            return ArrayExtensions.hasElements(productPrices) ? productPrices[0] : null;
                        })
                        .catch(() => {
                            return null;
                        });
                });

                var allPrices = await Promise.all(pricesPromises);

                return (allPrices?.filter(price => !!price) ?? []) as ProductPrice[];
            }

            return [] as ProductPrice[];
        })
        .catch((error: Error) => {
            ctx.trace(error.message);
            ctx.telemetry.exception(error);
            ctx.telemetry.debug('[getPriceForSelectedVariantAction]Error executing action');
            throw new Error('[getPriceForSelectedVariantAction]Error executing action');
        });
}

export const getPricesForSelectedVariantUnitOfMeasuresActionDataAction = createObservableDataAction({
    id: '@msdyn365-commerce-modules/retail-actions/get-price-for-selected-variant-uoms',
    action: getPricesForSelectedVariantUnitOfMeasures as IAction<ProductPrice[]>,
    input: createActivePriceForSelectedVariantUnitOfMeasuresInput
});

export default getPricesForSelectedVariantUnitOfMeasuresActionDataAction;
